import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, concatMap, filter, from, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { EditClinicalNoteModal } from 'src/app/pages/patient/components/clinical-notes-panel/modals/edit-clinical-note/edit-clinical-note.modal';
import { AlertService } from 'src/app/services/alert.service';
import { AttachmentsService } from 'src/app/services/attachments.service';
import { ClinicalNotesService } from 'src/app/services/clinical-notes.service';
import { DocumentInstancesService } from 'src/app/services/document-instances.service';
import { ErrorService } from 'src/app/services/error.service';
import { FileDownloadService } from 'src/app/services/file-download.service';
import { PatientsService } from 'src/app/services/patients.service';
import { clientErrored } from '../error/error.actions';
import { AppState } from '../reducers';
import * as patientClinicalNoteActions from './patient-clinical-note.actions';
import { patient } from '../patient-graph/patient-graph.selectors';
import { attachmentUploads, selectedClinicalNote, selectedCombinedClinicalNoteAttachments } from './patient-clinical-note.selectors';

@Injectable()
export class PatientClinicalNoteEffects {
	constructor(
		private actions$: Actions, private attachmentsService: AttachmentsService, private fileDownloadService: FileDownloadService,
		private store: Store<AppState>, private clinicalNotesService: ClinicalNotesService, private documentInstancesService: DocumentInstancesService,
		private patientsService: PatientsService, private errorService: ErrorService, private modalController: ModalController, private alertService: AlertService) {
	}

	addVisitClinicalNoteRequested$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientClinicalNoteActions.addClinicalNoteRequested),
			mergeMap(action => from(this.modalController.create({ component: EditClinicalNoteModal, backdropDismiss: false })).pipe(
				switchMap(modal => from(modal.present()).pipe(
					switchMap(() => this.clinicalNotesService.createClinicalNote(action.visitId, action.clinicalNoteDefinitionId).pipe(
						map(response => patientClinicalNoteActions.addClinicalNoteCompleted({ clinicalNoteId: response.clinicalNoteId })),
						catchError((error: HttpErrorResponse) => of(clientErrored({ toastMessage: 'Add clinical note failed', errorMessage: this.errorService.getErrorMessage(error) })))
					))
				))
			))
		)
	);

	editClinicalNoteSelected$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientClinicalNoteActions.editClinicalNoteSelected),
			concatLatestFrom(() => this.store.select(selectedClinicalNote)),
			mergeMap(() => from(this.modalController.create({ component: EditClinicalNoteModal, backdropDismiss: false })).pipe(
				switchMap(modal => from(modal.present())))
			)),
		{ dispatch: false }
	);

	exportClinicalNoteRequested$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientClinicalNoteActions.exportClinicalNoteRequested),
			mergeMap(action => this.clinicalNotesService.exportClinicalNote(action.clinicalNoteId, action.clinicalNoteVersion, action.documentInstanceVersion).pipe(
				map((response) => {
					var blob = response.body;
					if (blob) {
						this.fileDownloadService.downloadFile(blob, action.name);
						return patientClinicalNoteActions.exportClinicalNoteCompleted();
					}
					return clientErrored({ toastMessage: 'Export clinical note failed', errorMessage: 'Blob was null' });
				}),
				catchError((error: HttpErrorResponse) => of(clientErrored({ toastMessage: 'Export clinical note failed', errorMessage: this.errorService.getErrorMessage(error) })))
			))
		)
	);

	emailClinicalNoteRequested$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientClinicalNoteActions.emailClinicalNoteRequested),
			concatLatestFrom(() => this.store.select(patient)),
			mergeMap(([action, patient]) => this.patientsService.emailClinicalNote(patient!.id, {
				clinicalNoteId: action.clinicalNoteId,
				clinicalNoteVersion: action.clinicalNoteVersion,
				documentInstanceVersion: action.documentInstanceVersion,
				emailMessage: action.emailMessage,
				attachmentNameWithoutExtension: action.attachmentNameWithoutExtension
			}).pipe(
				map(() => patientClinicalNoteActions.emailClinicalNoteCompleted()),
				catchError((error: HttpErrorResponse) => of(clientErrored({ toastMessage: 'Email clinical note failed', errorMessage: this.errorService.getErrorMessage(error) })))
			))
		)
	);

	emailClinicalNoteCompleted$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientClinicalNoteActions.emailClinicalNoteCompleted),
			tap(() => this.alertService.displayToastAlert('Email sent'))
		),
		{ dispatch: false }
	);

	unlockClinicalNoteRequested$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientClinicalNoteActions.unlockClinicalNoteRequested),
			concatLatestFrom(() => this.store.select(selectedClinicalNote)),
			mergeMap(([action, clinicalNote]) => this.clinicalNotesService.unlockClinicalNote(clinicalNote!.id, action.reason).pipe(
				mergeMap(() => [patientClinicalNoteActions.unlockClinicalNoteCompleted(), patientClinicalNoteActions.editClinicalNoteSelected()]),
				catchError((error: HttpErrorResponse) => of(clientErrored({ toastMessage: 'Unlock clinical note failed', errorMessage: this.errorService.getErrorMessage(error) })))
			)
			))
	);

	updateClinicalNoteNameRequested$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientClinicalNoteActions.updateClinicalNoteNameRequested),
			concatMap(action => this.clinicalNotesService.updateClinicalNoteName(action.clinicalNoteId, action.name).pipe(
				map(() => patientClinicalNoteActions.updateClinicalNoteNameCompleted()),
				catchError((error: HttpErrorResponse) => of(clientErrored({ toastMessage: 'Update clinical note name failed', errorMessage: this.errorService.getErrorMessage(error) })))
			))
		)
	);

	setClinicalNoteDocumentWidgetValueRequested$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientClinicalNoteActions.setClinicalNoteDocumentWidgetValueRequested),
			concatMap(action => this.documentInstancesService.setWidgetValue(action.documentInstanceId, action.widgetId, action.value).pipe(
				map(() => patientClinicalNoteActions.setClinicalNoteDocumentWidgetValueCompleted()),
				catchError((error: HttpErrorResponse) => of(clientErrored({ toastMessage: 'Set clinical note document widget value failed', errorMessage: this.errorService.getErrorMessage(error) })))
			))
		)
	);

	attachmentUploadFailed$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientClinicalNoteActions.attachmentUploadFailed),
			map(action => clientErrored({ toastMessage: 'Attachment upload failed', errorMessage: action.errorMessage }))
		)
	);

	selectedCombinedClinicalNoteAttachments$ = createEffect(() =>
		this.store.select(selectedCombinedClinicalNoteAttachments).pipe(
			concatLatestFrom(() => this.store.select(attachmentUploads)),
			// get ids of attachments in uploads that also have an attachment in combined attachments
			map(([combinedAttachments, uploads]) => uploads.filter(upload => combinedAttachments.some(ca => ca.id === upload.id && ca.attachment)).map(upload => upload.id)),
			filter(uploadsThatHaveAttachments => uploadsThatHaveAttachments.length > 0),
			// remove them from uploads
			map(uploadsThatHaveAttachments => patientClinicalNoteActions.attachmentUploadsRemoved({ ids: uploadsThatHaveAttachments }))
		)
	);

	attachmentDownloadInitiated$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientClinicalNoteActions.attachmentDownloadInitiated),
			mergeMap(action => this.attachmentsService.getAttachmentFile(action.id).pipe(
				map((response) => {
					var blob = response.body;
					if (blob) {
						const contentDisposition = response.headers.get('Content-Disposition');
						const fileName = this.fileDownloadService.getFileName(contentDisposition) ?? 'attachment-file';
						this.fileDownloadService.downloadFile(blob, fileName);
						return patientClinicalNoteActions.attachmentDownloadCompleted();
					}
					return clientErrored({ toastMessage: 'Download attachment failed', errorMessage: 'Blob was null' });
				}),
				catchError((error: HttpErrorResponse) => of(clientErrored({ toastMessage: 'Download attachment failed', errorMessage: this.errorService.getErrorMessage(error) })))
			))
		)
	);

	removeAttachmentRequested$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientClinicalNoteActions.removeAttachmentRequested),
			mergeMap(action => this.attachmentsService.removeAttachment(action.id).pipe(
				map(() => patientClinicalNoteActions.removeAttachmentCompleted({ id: action.id })),
				catchError((error: HttpErrorResponse) => of(clientErrored({ toastMessage: 'Remove attachment failed', errorMessage: this.errorService.getErrorMessage(error) })))
			))
		)
	);
}